import React from 'react';
import { Helmet } from 'react-helmet-async';

const PolitiqueConfidentialite: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-900 text-white py-16 text-center mb-12 rounded-b-2xl">
        <h1 className="text-5xl font-bold mb-4">Politique de Confidentialité</h1>
        <p className="text-xl opacity-95 max-w-2xl mx-auto">
          Protection de vos données personnelles - TO THE ROAD
        </p>
      </header>

      <div className="max-w-4xl mx-auto px-4 pb-16">

        {/* Intro */}
        <div className="bg-white p-8 rounded-lg shadow-md border-l-4 border-blue-600 mb-10">
          <p className="mb-4 text-gray-900">
            <strong>TO THE ROAD</strong> accorde une grande importance à la protection de vos données
            personnelles et s'engage à les traiter dans le respect du Règlement Général sur la Protection
            des Données (RGPD) et de la loi Informatique et Libertés.
          </p>
          <p className="text-gray-900">
            Cette Politique de Confidentialité vous informe sur la manière dont nous collectons, utilisons,
            conservons et protégeons vos données personnelles lors de votre utilisation du site{' '}
            <strong>www.totheroad.fr</strong>.
          </p>
        </div>

        {/* Section 1 */}
        <section className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-6 pb-4 border-b-4 border-blue-200">
            1. Responsable du traitement
          </h2>
          <p className="mb-4 text-gray-900">
            Le responsable du traitement de vos données personnelles est :
          </p>
          <div className="bg-blue-100 p-6 rounded-lg">
            <p className="mb-2 text-gray-900"><strong>TO THE ROAD</strong></p>
            <p className="mb-2 text-gray-900">Société par Actions Simplifiée (SAS)</p>
            <p className="mb-2 text-gray-900">Siège social : 61 rue de Lyon, 75012 Paris, France</p>
            <p className="mb-2 text-gray-900">SIREN : 989 290 754 00013</p>
            <p className="text-gray-900">
              Email :{' '}
              <a href="mailto:contact@totheroad.fr" className="text-blue-600 hover:underline">
                contact@totheroad.fr
              </a>
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-6 pb-4 border-b-4 border-blue-200">
            2. Données personnelles collectées
          </h2>

          <h3 className="text-xl font-bold text-blue-600 mb-4">
            2.1. Données collectées via le formulaire de contact
          </h3>
          <p className="mb-3 text-gray-900">
            Lorsque vous utilisez notre formulaire de contact, nous collectons les informations suivantes :
          </p>
          <ul className="list-disc list-inside space-y-2 mb-4 text-gray-900">
            <li><strong>Nom et prénom</strong></li>
            <li><strong>Adresse email</strong></li>
            <li><strong>Numéro de portable</strong></li>
            <li><strong>Message</strong> (contenu de votre demande)</li>
          </ul>
          <p className="mb-1 text-gray-900"><strong>Base juridique :</strong> Votre consentement (Article 6.1.a RGPD)</p>
          <p className="mb-1 text-gray-900"><strong>Finalité :</strong> Répondre à vos demandes d'information</p>
          <p className="mb-6 text-gray-900"><strong>Durée de conservation :</strong> 3 ans à compter de votre dernier contact</p>

          <h3 className="text-xl font-bold text-blue-600 mb-4">
            2.2. Données collectées via l'inscription à la newsletter
          </h3>
          <p className="mb-3 text-gray-900">
            Lorsque vous vous inscrivez à notre newsletter, nous collectons :
          </p>
          <ul className="list-disc list-inside space-y-2 mb-4 text-gray-900">
            <li><strong>Adresse email</strong></li>
          </ul>
          <p className="mb-1 text-gray-900"><strong>Base juridique :</strong> Votre consentement (Article 6.1.a RGPD)</p>
          <p className="mb-1 text-gray-900"><strong>Finalité :</strong> Envoi de notre newsletter (actualités, nouveautés, offres)</p>
          <p className="mb-6 text-gray-900"><strong>Durée de conservation :</strong> Jusqu'à votre désinscription ou 3 ans d'inactivité</p>

          <h3 className="text-xl font-bold text-blue-600 mb-4">
            2.3. Données de navigation (Cookies)
          </h3>
          <p className="mb-3 text-gray-900">
            Lors de votre visite sur notre site, nous collectons automatiquement certaines données via des cookies :
          </p>
          <ul className="list-disc list-inside space-y-2 mb-4 text-gray-900">
            <li>Pages visitées</li>
            <li>Durée de visite</li>
            <li>Type de navigateur</li>
            <li>Adresse IP (anonymisée)</li>
          </ul>
          <p className="mb-1 text-gray-900">
            <strong>Base juridique :</strong> Consentement pour cookies analytiques, intérêt légitime pour cookies techniques
          </p>
          <p className="mb-1 text-gray-900"><strong>Finalité :</strong> Améliorer l'expérience utilisateur et analyser le trafic</p>
          <p className="mb-3 text-gray-900"><strong>Durée :</strong> Maximum 13 mois</p>
          <p className="text-gray-900">
            Pour plus d'informations, consultez notre{' '}
            <a href="/cookies" className="text-blue-600 hover:underline">Politique Cookies</a>.
          </p>
        </section>

        {/* Section 3 */}
        <section className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-6 pb-4 border-b-4 border-blue-200">
            3. Utilisation de vos données
          </h2>
          <p className="mb-6 text-gray-900">
            Nous utilisons vos données personnelles uniquement pour les finalités suivantes :
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 mb-4 rounded-r-lg">
            <p className="font-bold text-blue-900 mb-3">Formulaire de contact</p>
            <ul className="list-disc list-inside space-y-1 text-gray-900">
              <li>Répondre à vos questions</li>
              <li>Traiter vos demandes d'information</li>
              <li>Assurer le suivi de nos échanges</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 mb-4 rounded-r-lg">
            <p className="font-bold text-blue-900 mb-3">Newsletter</p>
            <ul className="list-disc list-inside space-y-1 text-gray-900">
              <li>Vous envoyer nos actualités</li>
              <li>Vous informer de nos nouveautés</li>
              <li>Vous proposer des contenus adaptés à vos centres d'intérêt</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-5 mb-6 rounded-r-lg">
            <p className="font-bold text-blue-900 mb-3">Analyse du site</p>
            <ul className="list-disc list-inside space-y-1 text-gray-900">
              <li>Mesurer l'audience du site</li>
              <li>Améliorer l'expérience utilisateur</li>
              <li>Optimiser le contenu et les fonctionnalités</li>
            </ul>
          </div>

          <p className="text-gray-900">
            <strong>Important :</strong> Nous ne vendons jamais vos données à des tiers. Nous ne les
            utilisons pas à des fins commerciales autres que celles mentionnées ci-dessus.
          </p>
        </section>

        {/* Section 4 */}
        <section className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-6 pb-4 border-b-4 border-blue-200">
            4. Partage de vos données
          </h2>

          <h3 className="text-xl font-bold text-blue-600 mb-4">4.1. Destinataires de vos données</h3>
          <p className="mb-3 text-gray-900">Vos données personnelles sont accessibles uniquement :</p>
          <ul className="list-disc list-inside space-y-2 mb-6 text-gray-900">
            <li><strong>En interne :</strong> Les collaborateurs habilités de TO THE ROAD</li>
            <li>
              <strong>Prestataires techniques :</strong>
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li><strong>Hostinger</strong> (hébergement du site)</li>
                <li><strong>Google Analytics</strong> (statistiques de visite - données anonymisées)</li>
              </ul>
            </li>
          </ul>

          <h3 className="text-xl font-bold text-blue-600 mb-4">4.2. Transferts hors Union Européenne</h3>
          <p className="mb-3 text-gray-900">
            Les données collectées via Google Analytics peuvent être transférées aux États-Unis.
          </p>
          <p className="mb-3 text-gray-900">
            <strong>Garanties :</strong> Google utilise les Clauses Contractuelles Types (CCT) approuvées
            par la Commission Européenne pour garantir la protection de vos données.
          </p>
          <p className="text-gray-900">
            Pour plus d'informations :{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Politique de confidentialité Google
            </a>
          </p>
        </section>

        {/* Section 5 */}
        <section className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-6 pb-4 border-b-4 border-blue-200">
            5. Durées de conservation
          </h2>
          <p className="mb-6 text-gray-900">
            Vos données sont conservées pendant les durées suivantes :
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-5 py-4 text-left border-b-2 border-blue-600 font-bold text-blue-900">
                    Type de données
                  </th>
                  <th className="px-5 py-4 text-left border-b-2 border-blue-600 font-bold text-blue-900">
                    Durée de conservation
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-5 py-4 border-b border-gray-200 text-gray-900">Formulaire de contact</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-gray-900">
                    <strong>3 ans</strong> après le dernier contact
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-5 py-4 border-b border-gray-200 text-gray-900">Newsletter</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-gray-900">
                    <strong>Jusqu'à désinscription</strong> ou <strong>3 ans d'inactivité</strong>
                  </td>
                </tr>
                <tr>
                  <td className="px-5 py-4 text-gray-900">Cookies</td>
                  <td className="px-5 py-4 text-gray-900"><strong>13 mois maximum</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-gray-900">
            À l'issue de ces durées, vos données sont automatiquement supprimées de nos systèmes.
          </p>
        </section>

        {/* Section 6 */}
        <section className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-6 pb-4 border-b-4 border-blue-200">
            6. Vos droits
          </h2>
          <p className="mb-6 text-gray-900">
            Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-3 rounded-r-lg">
            <p className="font-bold text-blue-900 mb-2">Droit d'accès (Article 15 RGPD)</p>
            <p className="text-gray-900">Vous pouvez demander une copie de toutes les données que nous détenons sur vous.</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-3 rounded-r-lg">
            <p className="font-bold text-blue-900 mb-2">Droit de rectification (Article 16 RGPD)</p>
            <p className="text-gray-900">Vous pouvez nous demander de corriger des données inexactes ou incomplètes.</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-3 rounded-r-lg">
            <p className="font-bold text-blue-900 mb-2">Droit à l'effacement (Article 17 RGPD)</p>
            <p className="text-gray-900">Vous pouvez demander la suppression de vos données (« droit à l'oubli »).</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-3 rounded-r-lg">
            <p className="font-bold text-blue-900 mb-2">Droit à la limitation (Article 18 RGPD)</p>
            <p className="text-gray-900">Vous pouvez demander le gel temporaire du traitement de vos données.</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-3 rounded-r-lg">
            <p className="font-bold text-blue-900 mb-2">Droit à la portabilité (Article 20 RGPD)</p>
            <p className="text-gray-900">
              Vous pouvez récupérer vos données dans un format structuré et les transmettre à un autre responsable.
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-8 rounded-r-lg">
            <p className="font-bold text-blue-900 mb-2">Droit d'opposition (Article 21 RGPD)</p>
            <p className="text-gray-900">
              Vous pouvez vous opposer au traitement de vos données, notamment pour la prospection commerciale
              (désinscription newsletter).
            </p>
          </div>

          <h3 className="text-xl font-bold text-blue-600 mb-4">Comment exercer vos droits ?</h3>
          <p className="mb-4 text-gray-900">Pour exercer l'un de ces droits, contactez-nous :</p>
          <div className="bg-blue-100 p-6 rounded-lg mb-4">
            <p className="mb-2 text-gray-900">
              <strong>Par e-mail :</strong>{' '}
              <a href="mailto:contact@totheroad.fr" className="text-blue-600 hover:underline">
                contact@totheroad.fr
              </a>
            </p>
            <p className="text-gray-900">
              <strong>Par courrier :</strong> TO THE ROAD — 61 rue de Lyon, 75012 Paris, France
            </p>
          </div>
          <p className="mb-8 text-gray-900">
            Nous vous répondrons dans un délai maximum d'<strong>1 mois</strong> à compter de la réception
            de votre demande.
          </p>

          <h3 className="text-xl font-bold text-blue-600 mb-4">Droit de réclamation auprès de la CNIL</h3>
          <p className="mb-4 text-gray-900">
            Si vous estimez que vos droits ne sont pas respectés, vous avez le droit d'introduire une
            réclamation auprès de la CNIL :
          </p>
          <div className="bg-blue-100 p-6 rounded-lg">
            <p className="mb-1 text-gray-900"><strong>CNIL</strong></p>
            <p className="mb-1 text-gray-900">3 Place de Fontenoy - TSA 80715</p>
            <p className="mb-1 text-gray-900">75334 PARIS CEDEX 07</p>
            <p className="mb-1 text-gray-900">Téléphone : 01 53 73 22 22</p>
            <p className="text-gray-900">
              Site web :{' '}
              <a
                href="https://www.cnil.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                www.cnil.fr
              </a>
            </p>
          </div>
        </section>

        {/* Section 7 */}
        <section className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-6 pb-4 border-b-4 border-blue-200">
            7. Sécurité de vos données
          </h2>
          <p className="mb-4 text-gray-900">
            Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées pour
            protéger vos données personnelles :
          </p>
          <ul className="list-disc list-inside space-y-3 mb-6 text-gray-900">
            <li><strong>Chiffrement SSL/TLS :</strong> Toutes les données échangées sont chiffrées (protocole HTTPS)</li>
            <li><strong>Accès restreint :</strong> Seuls les collaborateurs habilités ont accès à vos données</li>
            <li><strong>Sauvegardes régulières :</strong> Protection contre la perte de données</li>
            <li><strong>Pare-feu et protection anti-malware</strong></li>
          </ul>

          <h3 className="text-xl font-bold text-blue-600 mb-4">Hébergement sécurisé</h3>
          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
            <p className="mb-1 text-gray-900"><strong>Hébergeur :</strong> HOSTINGER INTERNATIONAL LTD</p>
            <p className="mb-1 text-gray-900"><strong>Localisation :</strong> Union Européenne</p>
            <p className="text-gray-900"><strong>Certifications :</strong> Conformité RGPD</p>
          </div>
        </section>

        {/* Section 8 */}
        <section className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-6 pb-4 border-b-4 border-blue-200">
            8. Cookies
          </h2>
          <p className="text-gray-900">
            Notre site utilise des cookies pour améliorer votre expérience de navigation. Pour plus
            d'informations sur les cookies que nous utilisons et comment les gérer, consultez notre{' '}
            <a href="/cookies" className="text-blue-600 hover:underline">
              Politique Cookies
            </a>
            .
          </p>
        </section>

        {/* Section 9 */}
        <section className="bg-white p-8 rounded-lg shadow-md mb-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-6 pb-4 border-b-4 border-blue-200">
            9. Modifications de la politique
          </h2>
          <p className="mb-4 text-gray-900">
            Nous nous réservons le droit de modifier cette Politique de Confidentialité à tout moment pour
            nous conformer aux évolutions législatives ou technologiques.
          </p>
          <p className="mb-4 text-gray-900">
            En cas de modification substantielle, nous vous en informerons par email (si vous êtes inscrit à
            la newsletter) ou par une notification sur notre site.
          </p>
          <p className="text-gray-900">
            <strong>Date de dernière mise à jour :</strong> 21 mars 2026
          </p>
        </section>

        {/* Final contact box */}
        <div className="bg-white p-8 rounded-lg shadow-md border-l-4 border-blue-600">
          <h3 className="text-2xl font-bold text-blue-900 mb-4">Besoin d'aide ?</h3>
          <p className="mb-4 text-gray-900">
            Pour toute question concernant cette Politique de Confidentialité ou l'utilisation de vos
            données personnelles, n'hésitez pas à nous contacter :
          </p>
          <p className="text-gray-900">
            <strong>Email :</strong>{' '}
            <a href="mailto:contact@totheroad.fr" className="text-blue-600 hover:underline">
              contact@totheroad.fr
            </a>
          </p>
        </div>

      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;
